import * as THREE from "three";
import * as CANNON from "cannon-es";

const BOT_RADIUS = 0.5;
const PATROL_SPEED = 2.1;
const STRAFE_SPEED = 1.35;
const ENGAGE_RANGE = 17;
const FIRE_INTERVAL_BASE = 1.3;
const BASE_DAMAGE = 16;

class BotsController {
  constructor({
    scene,
    world,
    collidables,
    waypoints,
    spawnPoints,
    flowerSpawner,
    getPlayerPosition,
    onPlayerHit,
    onBotBloomed,
    count = 3,
  }) {
    this.scene = scene;
    this.world = world;
    this.collidables = collidables;
    this.waypoints = waypoints;
    this.spawnPoints = spawnPoints;
    this.flowerSpawner = flowerSpawner;
    this.getPlayerPosition = getPlayerPosition;
    this.onPlayerHit = onPlayerHit;
    this.onBotBloomed = onBotBloomed;

    this.bots = [];
    this.raycaster = new THREE.Raycaster();
    this.toTarget = new THREE.Vector3();
    this.tmpVec = new THREE.Vector3();

    this.projectileGeo = new THREE.IcosahedronGeometry(0.065, 0);
    this.projectileMat = new THREE.MeshStandardMaterial({
      color: 0xf2b8c6,
      emissive: 0xd97a93,
      emissiveIntensity: 0.7,
      roughness: 0.5,
    });
    this.eyeMat = new THREE.MeshStandardMaterial({
      color: 0x22303a,
      emissive: 0x9fe8ff,
      emissiveIntensity: 1.1,
      roughness: 0.4,
    });
    this.projectilePool = [];
    this.activeProjectiles = [];

    const initialSpawns = [...spawnPoints];
    for (let i = 0; i < count; i += 1) {
      const spawnAt =
        initialSpawns[i % initialSpawns.length].clone().add(this.jitter(1.5));
      this.bots.push(this.createBot(i, spawnAt));
    }
  }

  jitter(amount) {
    return new THREE.Vector3(
      (Math.random() - 0.5) * amount * 2,
      0,
      (Math.random() - 0.5) * amount * 2
    );
  }

  createBot(id, position) {
    const ownGeometries = [];
    const body = new CANNON.Body({
      mass: 0,
      type: CANNON.Body.KINEMATIC,
      shape: new CANNON.Sphere(BOT_RADIUS),
      position: new CANNON.Vec3(position.x, BOT_RADIUS, position.z),
    });
    this.world.addBody(body);

    const group = new THREE.Group();
    const robeMat = new THREE.MeshStandardMaterial({
      color: 0x3d4660,
      roughness: 1,
      emissive: 0xff5d5d,
      emissiveIntensity: 0,
    });
    const robeGeo = new THREE.CylinderGeometry(0.26, 0.44, 1.05, 10);
    const robe = new THREE.Mesh(robeGeo, robeMat);
    robe.position.y = 0.52;
    robe.castShadow = true;
    group.add(robe);

    [-0.09, 0.09].forEach((x) => {
      const eyeGeo = new THREE.SphereGeometry(0.036, 8, 8);
      const eye = new THREE.Mesh(eyeGeo, this.eyeMat);
      eye.position.set(x, 0.86, 0.31);
      group.add(eye);
      ownGeometries.push(eyeGeo);
    });

    const head = this.flowerSpawner.buildFlowerMesh({
      height: 0.34,
      paletteIndex: id % 4,
      headScale: 1.05,
    });
    head.group.position.y = 1.02;
    group.add(head.group);

    group.traverse((child) => {
      child.userData.botId = id;
      child.userData.botAlive = true;
    });

    group.position.set(position.x, 0, position.z);
    this.scene.add(group);

    return {
      id,
      body,
      group,
      robeMat,
      headGroup: head.group,
      ownGeometries: [robeGeo, ...ownGeometries],
      hp: 4,
      maxHp: 4,
      yaw: Math.random() * Math.PI * 2,
      state: "patrol",
      waypoint: this.randomWaypoint(null),
      strafeDir: Math.random() > 0.5 ? 1 : -1,
      strafeFlipTimer: 1.5 + Math.random() * 2,
      fireTimer: 1.5 + Math.random() * 1.5,
      losTimer: Math.random() * 0.25,
      losClear: false,
      distToPlayer: Infinity,
      flashT: 0,
      dead: false,
      respawnTimer: 0,
      graceTimer: 0.8,
    };
  }

  randomWaypoint(exclude) {
    let wp = this.waypoints[Math.floor(Math.random() * this.waypoints.length)];
    let guard = 0;
    while (exclude && wp === exclude && guard < 8) {
      wp = this.waypoints[Math.floor(Math.random() * this.waypoints.length)];
      guard += 1;
    }
    return wp;
  }

  getHitTargets() {
    const targets = [];
    this.bots.forEach((bot) => {
      bot.group.traverse((child) => {
        if (child.isMesh && child.userData.botAlive !== false) targets.push(child);
      });
    });
    return targets;
  }

  applyDamage(id, hitPoint) {
    const bot = this.bots[id];
    if (!bot || bot.dead) return;
    bot.hp -= 1;
    bot.flashT = 0.2;
    const playerPos = this.getPlayerPosition();
    this.toTarget
      .set(bot.body.position.x - playerPos.x, 0, bot.body.position.z - playerPos.z)
      .normalize()
      .multiplyScalar(2.2);
    bot.body.velocity.x += this.toTarget.x;
    bot.body.velocity.z += this.toTarget.z;

    if (bot.hp <= 0) {
      this.killBot(bot);
    } else {
      bot.state = "engage";
    }
  }

  killBot(bot) {
    bot.dead = true;
    bot.group.visible = false;
    bot.group.traverse((child) => {
      child.userData.botAlive = false;
    });
    this.world.removeBody(bot.body);
    bot.respawnTimer = 3.8;
    const feet = new THREE.Vector3(
      bot.body.position.x,
      0,
      bot.body.position.z
    );
    this.flowerSpawner.spawnBurst(feet, 12);
    if (this.onBotBloomed) this.onBotBloomed(bot.id);
  }

  respawnBot(bot) {
    const playerPos = this.getPlayerPosition();
    let best = this.spawnPoints[0];
    let bestDist = -1;
    this.spawnPoints.forEach((sp) => {
      const d = sp.distanceToSquared(playerPos);
      if (d > bestDist) {
        bestDist = d;
        best = sp;
      }
    });

    bot.hp = bot.maxHp;
    bot.dead = false;
    bot.graceTimer = 1;
    bot.fireTimer = 1.2 + Math.random();
    bot.state = "patrol";
    bot.waypoint = this.randomWaypoint(null);
    bot.body.position.set(best.x + this.jitter(1).x, BOT_RADIUS, best.z + this.jitter(1).z);
    bot.body.velocity.set(0, 0, 0);
    bot.group.visible = true;
    bot.group.traverse((child) => {
      child.userData.botAlive = true;
    });
    this.world.addBody(bot.body);
  }

  checkLineOfSight(bot) {
    const playerPos = this.getPlayerPosition();
    const origin = new THREE.Vector3(
      bot.body.position.x,
      1.35,
      bot.body.position.z
    );
    const chest = playerPos.clone();
    chest.y += 0.55;
    this.toTarget.copy(chest).sub(origin);
    const dist = this.toTarget.length();
    bot.distToPlayer = dist;
    if (dist > ENGAGE_RANGE) {
      bot.losClear = false;
      return;
    }
    this.raycaster.set(origin, this.toTarget.normalize());
    this.raycaster.far = dist - 0.4;
    const blockers = this.raycaster.intersectObjects(this.collidables, false);
    bot.losClear = blockers.length === 0;
  }

  faceTowards(bot, targetX, targetZ, dt, turnSpeed = 6) {
    const dx = targetX - bot.body.position.x;
    const dz = targetZ - bot.body.position.z;
    const targetYaw = Math.atan2(dx, dz);
    let delta = targetYaw - bot.yaw;
    delta = Math.atan2(Math.sin(delta), Math.cos(delta));
    bot.yaw += delta * Math.min(1, dt * turnSpeed);
  }

  update(dt, elapsed) {
    const playerPos = this.getPlayerPosition();

    this.bots.forEach((bot) => {
      if (bot.flashT > 0) {
        bot.flashT = Math.max(0, bot.flashT - dt);
        bot.robeMat.emissiveIntensity = bot.flashT * 3;
      }
      bot.headGroup.rotation.y += dt * 0.6;
      bot.headGroup.rotation.x =
        Math.sin(elapsed * 1.4 + bot.id) * 0.08;

      if (bot.dead) {
        bot.respawnTimer -= dt;
        if (bot.respawnTimer <= 0) this.respawnBot(bot);
        return;
      }

      if (bot.graceTimer > 0) bot.graceTimer -= dt;

      bot.losTimer -= dt;
      if (bot.losTimer <= 0) {
        bot.losTimer = 0.2 + Math.random() * 0.1;
        this.checkLineOfSight(bot);
      }

      const engaged = bot.losClear && bot.distToPlayer < ENGAGE_RANGE;
      bot.state = engaged ? "engage" : "patrol";
      let vx = 0;
      let vz = 0;

      if (engaged) {
        this.faceTowards(bot, playerPos.x, playerPos.z, dt, 8);
        bot.strafeFlipTimer -= dt;
        if (bot.strafeFlipTimer <= 0) {
          bot.strafeFlipTimer = 1.5 + Math.random() * 2;
          if (Math.random() > 0.5) bot.strafeDir *= -1;
        }
        this.toTarget
          .set(playerPos.x - bot.body.position.x, 0, playerPos.z - bot.body.position.z)
          .normalize();
        const side = new THREE.Vector3(-this.toTarget.z, 0, this.toTarget.x).multiplyScalar(
          bot.strafeDir
        );
        let radial = 0;
        if (bot.distToPlayer < 6) radial = -1;
        else if (bot.distToPlayer > 12) radial = 1;
        vx = (side.x + this.toTarget.x * radial) * STRAFE_SPEED;
        vz = (side.z + this.toTarget.z * radial) * STRAFE_SPEED;

        if (bot.losClear && bot.graceTimer <= 0) {
          bot.fireTimer -= dt;
          if (bot.fireTimer <= 0) {
            bot.fireTimer = FIRE_INTERVAL_BASE + Math.random() * 1.0;
            this.shootAtPlayer(bot, playerPos);
          }
        }
      } else {
        const wp = bot.waypoint;
        const dx = wp.x - bot.body.position.x;
        const dz = wp.z - bot.body.position.z;
        if (dx * dx + dz * dz < 0.81) {
          bot.waypoint = this.randomWaypoint(wp);
        } else {
          const len = Math.sqrt(dx * dx + dz * dz);
          vx = (dx / len) * PATROL_SPEED;
          vz = (dz / len) * PATROL_SPEED;
          this.faceTowards(bot, wp.x, wp.z, dt, 5);
        }
      }

      if (vx !== 0 || vz !== 0) {
        bot.body.wakeUp();
        const px = bot.body.position.x;
        const pz = bot.body.position.z;
        const nx = px + vx * dt;
        const nz = pz + vz * dt;
        const PLATFORM_RADIUS = 7.2;
        if (nx * nx + nz * nz < PLATFORM_RADIUS * PLATFORM_RADIUS) {
          const speed = Math.hypot(vx, vz);
          let tx = -pz;
          let tz = px;
          const tangentLength = Math.hypot(tx, tz) || 1;
          const sign = tx * vx + tz * vz >= 0 ? 1 : -1;
          vx = (tx / tangentLength) * speed * sign;
          vz = (tz / tangentLength) * speed * sign;
        }
      }

      bot.body.velocity.x = vx;
      bot.body.velocity.z = vz;
    });

    this.updateProjectiles(dt);

    this.bots.forEach((bot) => {
      if (!bot.dead) {
        bot.group.position.set(
          bot.body.position.x,
          bot.body.position.y - BOT_RADIUS,
          bot.body.position.z
        );
        bot.group.rotation.y = bot.yaw;
      }
    });
  }

  shootAtPlayer(bot, playerPos) {
    const accuracy = THREE.MathUtils.clamp(0.72 - bot.distToPlayer * 0.022, 0.14, 0.72);
    const willHit = Math.random() < accuracy;
    const from = new THREE.Vector3(
      bot.body.position.x,
      bot.body.position.y + 0.85,
      bot.body.position.z
    );
    const target = playerPos.clone();
    target.y += 0.55;
    if (!willHit) {
      const miss = this.jitter(1).normalize().multiplyScalar(0.7 + Math.random() * 0.9);
      target.add(miss);
    }
    const dur = THREE.MathUtils.clamp(from.distanceTo(target) / 26, 0.18, 0.85);

    let mesh = this.projectilePool.pop();
    if (!mesh) mesh = new THREE.Mesh(this.projectileGeo, this.projectileMat);
    mesh.visible = true;
    mesh.position.copy(from);
    this.scene.add(mesh);

    this.activeProjectiles.push({ mesh, from, to: target, t: 0, dur, willHit, botId: bot.id });
  }

  updateProjectiles(dt) {
    for (let i = this.activeProjectiles.length - 1; i >= 0; i -= 1) {
      const p = this.activeProjectiles[i];
      p.t += dt / p.dur;
      if (p.t >= 1) {
        if (p.willHit && this.onPlayerHit) {
          this.onPlayerHit(BASE_DAMAGE, p.botId);
        }
        if (p.mesh.parent) p.mesh.parent.remove(p.mesh);
        if (this.projectilePool.length < 16) this.projectilePool.push(p.mesh);
        else p.mesh.visible = false;
        this.activeProjectiles.splice(i, 1);
      } else {
        p.mesh.position.lerpVectors(p.from, p.to, p.t);
        p.mesh.rotation.x += dt * 12;
        p.mesh.rotation.y += dt * 9;
      }
    }
  }

  dispose() {
    this.bots.forEach((bot) => {
      this.scene.remove(bot.group);
      bot.ownGeometries.forEach((geo) => geo.dispose());
      bot.robeMat.dispose();
      if (!bot.dead) this.world.removeBody(bot.body);
    });
    this.bots = [];
    this.activeProjectiles.forEach((p) => {
      if (p.mesh.parent) p.mesh.parent.remove(p.mesh);
    });
    this.projectilePool.forEach((mesh) => {
      if (mesh.parent) mesh.parent.remove(mesh);
    });
    this.projectileGeo.dispose();
    this.projectileMat.dispose();
    this.eyeMat.dispose();
    this.activeProjectiles = [];
    this.projectilePool = [];
  }
}

export { BotsController };
export default BotsController;
